/**
 * ReportConfigsCard — manage named report parameter sets.
 *
 * Phase 4.3.GAPCONF: now supports all 10 ALM report types and gapConfigId.
 * Mounted standalone on /settings/report-configs (and still on AlmParametersPage
 * temporarily during transition).
 */
import { useState } from 'react';
import {
  AlertCircle, CheckCircle2, ChevronDown, ChevronRight,
  Clock, Edit2, Loader2, Play, Plus, Trash2, X,
} from 'lucide-react';
import { Button }      from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input }  from '@/components/ui/input';
import { Label }  from '@/components/ui/label';
import { CubixApiError } from '@/lib/api/types';
import { useAuthStore }  from '@/lib/auth/store';

import {
  useReportConfigs,
  useCreateReportConfig,
  useUpdateReportConfig,
  useDeleteReportConfig,
  useRunReportConfig,
  useReportSnapshots,
  TYPE_LABELS,
} from './reportConfigApi';
import type {
  ReportConfig,
  ReportConfigCreate,
  ReportParams,
  GapMatrixParams,
  NimParams,
  ReportSnapshot,
  ReportType,
} from './reportConfigApi';


// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  SUCCESS: 'text-emerald-600',
  FAILED:  'text-red-500',
  RUNNING: 'text-amber-500',
};

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function apiErr(e: unknown): string {
  if (e instanceof CubixApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Unexpected error';
}

// ── Snapshot history sub-component ───────────────────────────────────────────

function SnapshotHistory({ configId }: { configId: string }) {
  const { data, isLoading } = useReportSnapshots(configId);

  if (isLoading) return (
    <div className="px-4 py-2 text-sm text-gray-400 flex items-center gap-2">
      <Loader2 className="h-3 w-3 animate-spin" /> Loading history…
    </div>
  );

  if (!data?.length) return (
    <div className="px-4 py-2 text-sm text-gray-400 italic">No runs yet.</div>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs text-left">
        <thead>
          <tr className="border-b text-gray-500">
            <th className="px-3 py-1.5 font-medium">Triggered at</th>
            <th className="px-3 py-1.5 font-medium">As-of date</th>
            <th className="px-3 py-1.5 font-medium">By</th>
            <th className="px-3 py-1.5 font-medium">Status</th>
            <th className="px-3 py-1.5 font-medium text-right">Duration</th>
          </tr>
        </thead>
        <tbody>
          {data.map((snap: ReportSnapshot) => (
            <tr key={snap.id} className="border-b hover:bg-gray-50">
              <td className="px-3 py-1.5 whitespace-nowrap">{formatDate(snap.triggeredAt)}</td>
              <td className="px-3 py-1.5">{snap.asOfDate}</td>
              <td className="px-3 py-1.5">
                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                  snap.triggeredBy === 'SCHEDULED'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {snap.triggeredBy}
                </span>
              </td>
              <td className={`px-3 py-1.5 font-semibold ${STATUS_COLORS[snap.status] ?? ''}`}>
                {snap.status}
                {snap.status === 'FAILED' && snap.errorMessage && (
                  <span className="ml-1 text-gray-400 font-normal" title={snap.errorMessage}>⚠</span>
                )}
              </td>
              <td className="px-3 py-1.5 text-right text-gray-500">
                {snap.durationMs != null ? `${snap.durationMs} ms` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Config row ────────────────────────────────────────────────────────────────

interface ConfigRowProps {
  config: ReportConfig;
  isAdmin: boolean;
  onEdit: (c: ReportConfig) => void;
  onDelete: (id: string) => void;
  onRun: (id: string) => void;
  running: boolean;
}

function ConfigRow({ config, isAdmin, onEdit, onDelete, onRun, running }: ConfigRowProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border rounded-md overflow-hidden">
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 cursor-pointer"
        onClick={() => setOpen(v => !v)}
      >
        {open ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
               : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{config.name}</span>
            {config.isPreset && (
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">★ Preset</span>
            )}
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
              config.reportType === 'GAP_MATRIX'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-teal-100 text-teal-700'
            }`}>
              {TYPE_LABELS[config.reportType]}
            </span>
            {config.scheduleActive && (
              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                <Clock className="h-3 w-3" /> {config.cronExpression}
              </span>
            )}
          </div>
          {config.description && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{config.description}</p>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
          {/* Run now */}
          <Button
            size="sm"
            variant="outline"
            disabled={running}
            onClick={() => onRun(config.id)}
            className="h-7 px-2 text-xs"
            title="Run now"
          >
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
          </Button>

          {/* Edit / Delete — non-preset, admin only */}
          {!config.isPreset && isAdmin && (
            <>
              <Button size="sm" variant="ghost" className="h-7 px-1.5"
                      onClick={() => onEdit(config)} title="Edit">
                <Edit2 className="h-3.5 w-3.5 text-gray-500" />
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-1.5"
                      onClick={() => onDelete(config.id)} title="Delete">
                <Trash2 className="h-3.5 w-3.5 text-red-400" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Expanded: snapshot history */}
      {open && (
        <div className="border-t bg-gray-50">
          <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Run history
          </div>
          <SnapshotHistory configId={config.id} />
        </div>
      )}
    </div>
  );
}

// ── Default params per type ───────────────────────────────────────────────────

function defaultParams(type: ReportType): ReportParams {
  if (type === 'GAP_MATRIX') {
    return { dateMode: 'LAST_EOD', specificDate: null } as GapMatrixParams;
  }
  if (type === 'GAP_TIMESERIES') {
    return { dateMode: 'LAST_30D', specificFrom: null, specificTo: null };
  }
  return { dateMode: 'LAST_EOD', specificDate: null } as NimParams;
}

// ── Edit / Create form ────────────────────────────────────────────────────────

interface FormState {
  name: string;
  description: string;
  reportType: ReportType;
  gapConfigId: string | null;
  parameters: ReportParams;
  isScheduled: boolean;
  cronExpression: string;
  scheduleActive: boolean;
}

function blankForm(): FormState {
  return {
    name: '', description: '', reportType: 'GAP_MATRIX',
    gapConfigId: null,
    parameters: defaultParams('GAP_MATRIX'),
    isScheduled: false, cronExpression: '', scheduleActive: false,
  };
}

function fromConfig(c: ReportConfig): FormState {
  return {
    name:           c.name,
    description:    c.description ?? '',
    reportType:     c.reportType,
    gapConfigId:    c.gapConfigId ?? null,
    parameters:     c.parameters,
    isScheduled:    c.isScheduled,
    cronExpression: c.cronExpression ?? '',
    scheduleActive: c.scheduleActive,
  };
}

interface ParamsFormProps {
  type: ReportType;
  params: ReportParams;
  onChange: (p: ReportParams) => void;
}

function ParamsForm({ type, params, onChange }: ParamsFormProps) {
  const set = (key: string, val: string | null) =>
    onChange({ ...params, [key]: val || null });

  const gp = params as GapMatrixParams;
  const np = params as NimParams;

  return (
    <div className="space-y-3">
      {type === 'GAP_MATRIX' && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Dimension</Label>
            <select
              className="w-full border rounded-md px-2 py-1.5 text-sm bg-white"
              value={gp.rowDimension ?? 'INSTRUMENT_TYPE'}
              onChange={e => set('rowDimension', e.target.value)}
            >
              <option value="INSTRUMENT_TYPE">Instrument type</option>
              <option value="BS_CLASS">BS class</option>
              <option value="CURRENCY">Currency</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Currency filter</Label>
            <Input
              className="text-sm"
              placeholder="MAD (all if empty)"
              value={gp.currency ?? ''}
              onChange={e => set('currency', e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Segmentation ID</Label>
            <Input
              className="text-sm font-mono"
              placeholder="UUID (optional)"
              value={gp.segmentationId ?? ''}
              onChange={e => set('segmentationId', e.target.value)}
            />
          </div>
        </div>
      )}

      {type === 'NIM' && (
        <div className="space-y-1">
          <Label className="text-xs">Currency filter</Label>
          <Input
            className="text-sm"
            placeholder="MAD (all if empty)"
            value={np.currency ?? ''}
            onChange={e => set('currency', e.target.value)}
          />
        </div>
      )}

      {/* Date mode — shared */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Date mode</Label>
          <select
            className="w-full border rounded-md px-2 py-1.5 text-sm bg-white"
            value={(params as GapMatrixParams).dateMode ?? 'LAST_EOD'}
            onChange={e => set('dateMode', e.target.value)}
          >
            <option value="LAST_EOD">Last EOD (latest available)</option>
            <option value="SPECIFIC">Specific date</option>
          </select>
        </div>
        {(params as GapMatrixParams).dateMode === 'SPECIFIC' && (
          <div className="space-y-1">
            <Label className="text-xs">Date</Label>
            <Input
              type="date"
              className="text-sm"
              value={(params as GapMatrixParams).specificDate ?? ''}
              onChange={e => set('specificDate', e.target.value)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main card ─────────────────────────────────────────────────────────────────

export function ReportConfigsCard() {
  const isAdmin = useAuthStore(s => s.hasRole('ALM_ADMIN'));

  const { data: configs, isLoading, isError } = useReportConfigs();
  const createMut  = useCreateReportConfig();
  const updateMut  = useUpdateReportConfig();
  const deleteMut  = useDeleteReportConfig();
  const runMut     = useRunReportConfig();

  const [editId,    setEditId]    = useState<string | null>(null);
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState<FormState>(blankForm());
  const [formError, setFormError] = useState('');
  const [success,   setSuccess]   = useState('');
  const [runningId, setRunningId] = useState<string | null>(null);

  const presets = configs?.filter(c => c.isPreset)  ?? [];
  const custom  = configs?.filter(c => !c.isPreset) ?? [];

  // ── Handlers ────────────────────────────────────────────────────────────────

  function openCreate() {
    setEditId(null);
    setForm(blankForm());
    setFormError('');
    setShowForm(true);
  }

  function openEdit(c: ReportConfig) {
    setEditId(c.id);
    setForm(fromConfig(c));
    setFormError('');
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditId(null);
    setFormError('');
  }

  async function handleSubmit() {
    setFormError('');
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    if (form.isScheduled && !form.cronExpression.trim()) {
      setFormError('Cron expression is required when scheduling is enabled');
      return;
    }

    const body: ReportConfigCreate = {
      name:           form.name.trim(),
      description:    form.description.trim() || undefined,
      reportType:     form.reportType,
      gapConfigId:    form.gapConfigId ?? undefined,
      parameters:     form.parameters,
      isScheduled:    form.isScheduled,
      cronExpression: form.isScheduled ? form.cronExpression.trim() : null,
      scheduleActive: form.isScheduled && form.scheduleActive,
    };

    try {
      if (editId) {
        await updateMut.mutateAsync({ id: editId, ...body });
        setSuccess('Report config updated.');
      } else {
        await createMut.mutateAsync(body);
        setSuccess('Report config created.');
      }
      closeForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setFormError(apiErr(e));
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this report config? All snapshot history will also be deleted.')) return;
    try {
      await deleteMut.mutateAsync(id);
      setSuccess('Deleted.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setSuccess('');
      setFormError(apiErr(e));
    }
  }

  async function handleRun(id: string) {
    setRunningId(id);
    try {
      const snap = await runMut.mutateAsync(id);
      setSuccess(`Run ${snap.status.toLowerCase()} in ${snap.durationMs ?? '?'} ms.`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (e) {
      setFormError(apiErr(e));
    } finally {
      setRunningId(null);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="text-base">Report configurations</CardTitle>
          <CardDescription>
            Define standard and custom report parameters. Preset configs are read-only.
            Custom configs support scheduling with a cron expression.
          </CardDescription>
        </div>
        {isAdmin && !showForm && (
          <Button size="sm" variant="outline" onClick={openCreate} className="shrink-0 ml-4">
            <Plus className="h-4 w-4 mr-1" /> New config
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">

        {/* Global feedback */}
        {success && (
          <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> {success}
          </div>
        )}
        {formError && !showForm && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            <AlertCircle className="h-4 w-4 shrink-0" /> {formError}
          </div>
        )}

        {/* Loading / error */}
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {isError && (
          <div className="text-sm text-red-500">Failed to load report configs.</div>
        )}

        {/* ── Preset section ──────────────────────────────────────────────── */}
        {presets.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Standard reports
            </h4>
            <div className="space-y-2">
              {presets.map(c => (
                <ConfigRow
                  key={c.id} config={c} isAdmin={isAdmin}
                  onEdit={openEdit} onDelete={handleDelete}
                  onRun={handleRun} running={runningId === c.id}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Custom section ──────────────────────────────────────────────── */}
        {(custom.length > 0 || (!showForm && isAdmin)) && (
          <section>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Custom reports
            </h4>
            {custom.length === 0 && !showForm && (
              <p className="text-sm text-gray-400 italic">
                No custom configs yet. Click "New config" to create one.
              </p>
            )}
            <div className="space-y-2">
              {custom.map(c => (
                <ConfigRow
                  key={c.id} config={c} isAdmin={isAdmin}
                  onEdit={openEdit} onDelete={handleDelete}
                  onRun={handleRun} running={runningId === c.id}
                />
              ))}
            </div>
          </section>
        )}

        {/* ── Create / Edit form ──────────────────────────────────────────── */}
        {showForm && (
          <div className="border rounded-md p-4 bg-gray-50 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">
                {editId ? 'Edit report config' : 'New report config'}
              </h4>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            {formError && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
                <AlertCircle className="h-4 w-4 shrink-0" /> {formError}
              </div>
            )}

            {/* Basic fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Name *</Label>
                <Input
                  className="text-sm"
                  placeholder="e.g. Matrice BAM mensuelle"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs">Description</Label>
                <Input
                  className="text-sm"
                  placeholder="Optional description"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Report type *</Label>
                <select
                  className="w-full border rounded-md px-2 py-1.5 text-sm bg-white"
                  disabled={!!editId}
                  value={form.reportType}
                  onChange={e => {
                    const t = e.target.value as ReportType;
                    setForm(f => ({ ...f, reportType: t, parameters: defaultParams(t) }));
                  }}
                >
                  <option value="GAP_MATRIX">Gap Matrix</option>
                  <option value="GAP_TIMESERIES">Gap Time Series</option>
                  <option value="NIM">NIM</option>
                  <option value="EVE">EVE / KRD</option>
                  <option value="DURATION">Duration / DV01</option>
                  <option value="LCR">LCR</option>
                  <option value="NSFR">NSFR</option>
                  <option value="FTP">FTP</option>
                  <option value="STRESS">Stress test</option>
                  <option value="BAM">Rapport BAM</option>
                </select>
              </div>
            </div>

            {/* Type-specific params */}
            <div className="border-t pt-3">
              <Label className="text-xs text-gray-500 mb-2 block">Report parameters</Label>
              <ParamsForm
                type={form.reportType}
                params={form.parameters}
                onChange={p => setForm(f => ({ ...f, parameters: p }))}
              />
            </div>

            {/* Scheduling */}
            <div className="border-t pt-3 space-y-3">
              <Label className="text-xs text-gray-500 block">Scheduling</Label>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-scheduled"
                  checked={form.isScheduled}
                  onChange={e => setForm(f => ({
                    ...f,
                    isScheduled:    e.target.checked,
                    scheduleActive: e.target.checked && f.scheduleActive,
                  }))}
                />
                <label htmlFor="is-scheduled" className="text-sm cursor-pointer">
                  Enable cron schedule
                </label>
              </div>

              {form.isScheduled && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Cron expression *</Label>
                    <Input
                      className="text-sm font-mono"
                      placeholder="0 0 8 * * MON-FRI"
                      value={form.cronExpression}
                      onChange={e => setForm(f => ({ ...f, cronExpression: e.target.value }))}
                    />
                    <p className="text-xs text-gray-400">
                      Spring 6-field cron: second minute hour day month weekday
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-5">
                    <input
                      type="checkbox"
                      id="sched-active"
                      checked={form.scheduleActive}
                      onChange={e => setForm(f => ({ ...f, scheduleActive: e.target.checked }))}
                    />
                    <label htmlFor="sched-active" className="text-sm cursor-pointer">
                      Active (start running immediately)
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Form actions */}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={closeForm}>Cancel</Button>
              <Button
                size="sm"
                disabled={createMut.isPending || updateMut.isPending}
                onClick={handleSubmit}
              >
                {(createMut.isPending || updateMut.isPending)
                  ? <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Saving…</>
                  : editId ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
